import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { FormDataContainer, SettingsWrapper } from "./styled";
import {
  Box,
  ButtonStyled,
  DataContainerHeader,
  FormElementWrapper,
  LabelStyled,
  SelectionDropdown,
  SeparatorBorder,
  SubHeadingStyled,
  ThemeLoader,
} from "../../../../components/styled/Elements";
import { Col, Form, Row } from "react-bootstrap";
import ValidationErrorText from "../../../../components/common/inputFields/ValidationErrorText";
import ValidatedInput from "../../../../components/common/inputFields/ValidatedInput";
import { LazyLoadDropdown } from "../../../../components/common/inputFields/LazyLoadDropdown";
import PhoneInputField from "../../../../components/common/inputFields/PhoneInput";
import ValidationController from "../../../../components/common/inputFields/ValidationController";
import ImageUpload from "../../../../components/common/uploadFiles/imagesUpload";
import { BackArrow } from "../../../../components/styled/AllImages";
import SettingsAPI from "../../../../services/api/settingsApi";
import { toast } from "react-toastify";
import { useUser } from "../../../../context/userContext";

// const currencyOptions = [
//   { value: "USD", label: "US Dollar (USD)" },
//   { value: "EUR", label: "Euro (EUR)" },
//   { value: "GBP", label: "British Pound (GBP)" },
//   { value: "JPY", label: "Japanese Yen (JPY)" },
//   { value: "PKR", label: "Pakistani Rupee (PKR)" },
//   { value: "INR", label: "Indian Rupee (INR)" },
//   { value: "CNY", label: "Chinese Yuan (CNY)" },
//   { value: "AUD", label: "Australian Dollar (AUD)" },
//   { value: "CAD", label: "Canadian Dollar (CAD)" },
//   { value: "CHF", label: "Swiss Franc (CHF)" },
//   { value: "SAR", label: "Saudi Riyal (SAR)" },
//   { value: "AED", label: "UAE Dirham (AED)" },
// ];

const EditEstablishment = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { fetchUserInfo } = useUser();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    watch,
    control,
    reset,
    formState: { errors },
  } = useForm();

  const countryId = watch("country_id");

  // State for dropdowns
  const [typeOptions, setTypeOptions] = useState([
    {
      value: 1,
      label: "organization",
    },
    {
      value: 2,
      label: "individual",
    },
  ]); // Type options from API
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [filteredCurrencies, setFilteredCurrencies] = useState([]);
  const [logoDelete, setLogoDelete] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const location = useLocation();
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const handleSearch = (searchQuery) => {
    fetchCities(countryId, currentPage, searchQuery);
  };
  const handleCurrencySearch = (searchQuery) => {
    setFilteredCurrencies(
      currencyOptions.filter((item) => {
        return item?.label.toLowerCase().includes(searchQuery);
      })
    );
  };

  const handleLoadMore = () => {
    fetchCities(countryId, currentPage + 1);
  };

  const getEstablishmentInfo = async () => {
    try {
      setIsFetching(true);
      const { data, code } = await SettingsAPI.getEstablishmentInfo();
      if (code === 200) {
        reset({
          name: data.name || "",
          organization_type_id: data.organization_type_id || "",
          sector: data.sector || "",
          description: data.description || "",
          country_id: data.country_id || "",
          currency_id: data?.currency?.id ? parseInt(data?.currency?.id) : "",
          vat_number: data?.vat_number || "",
          vat_percentage: data?.vat_percentage || "",
          city_id: data?.city_id ? parseInt(data?.city_id) : "",
          address: data.address || "",
          contact_phone: data.contact_phone || data.phone || "",
          contact_email: data.contact_email || data.email || "",
          // logo: data?.logo || null,
        });
        setLogoFile(
          data?.logo ? `${process.env.REACT_APP_ASSET_URL}/${data?.logo}` : null
        );
      }
    } catch (error) {
    } finally {
      setIsFetching(false);
    }
  };

  const fetchCountriesCurrencies = async () => {
    try {
      const response = await SettingsAPI.getCountriesList();
      if (response.status) {
        const countryOptions = response?.data?.countries?.map((country) => ({
          value: country.id,
          label: `${language === "ar" ? country.ar_name : country.en_name} ${
            country.emoji
          }`,
        }));
        const currencies = response?.data?.currencies?.map((currency) => ({
          value: currency?.id,
          label: currency?.stringCode,
        }));
        setCountries(countryOptions);
        setCurrencyOptions(currencies);
      }
    } catch (error) {}
  };
  const fetchCities = async (id, page = 1, query) => {
    setCitiesLoading(true);
    try {
      const response = await SettingsAPI.getCitiesList(id, page, query);
      if (response.status) {
        const cityOptions = response?.data?.data?.map((country) => ({
          value: country.id,
          label: `${language === "ar" ? country.ar_name : country.en_name}`,
        }));
        if (query) {
          setFilteredCities(cityOptions);
        } else setCities(cityOptions);
        setHasMore(!!response.data.next_page_url);
        setCurrentPage(page);
      }
    } catch (error) {
    } finally {
      setCitiesLoading(false);
    }
  };

  useEffect(() => {
    if (countryId) fetchCities(parseInt(countryId));
  }, [countryId, language]);

  useEffect(() => {
    fetchCountriesCurrencies();
    getEstablishmentInfo();
  }, [language]);

  const onSubmit = async (formData) => {
    try {
      setLoading(true);

      // Create a FormData object
      const updatedData = new FormData();
      updatedData.append("action", "create");
      updatedData.append("logo_delete", logoDelete);

      // Append form fields to FormData
      Object.entries(formData).forEach(([key, value]) => {
        if (!value) {
          updatedData.delete(key);
        } else updatedData.append(key, value);
      });
      const { message, code } = await SettingsAPI.updateEstablishment(
        updatedData
      );
      if (code === 200) {
        toast.success(t("establishment_updated"));
        fetchUserInfo();
        navigate("/settings/establishment");
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <SettingsWrapper>
      <Box className="content-head">
        <SubHeadingStyled size="16px" color="var(--text)">
          {t("settings")}
        </SubHeadingStyled>
      </Box>
      {dataLoading ? (
        <ThemeLoader />
      ) : (
        <Box className="content-container">
          {isFetching ? (
            <ThemeLoader />
          ) : (
            <Row className="mt-3">
              <Col md={12}>
                <Form onSubmit={handleSubmit(onSubmit)}>
                  <FormDataContainer className="dataContainer">
                    <DataContainerHeader
                      backBtnLabel={t("establishment")}
                      noBreadcrumbs=""
                      icon={BackArrow}
                      onClickBack={() => navigate("/settings/establishment")}
                    />

                    <ValidatedInput
                      name="name"
                      type="input"
                      fieldType="text"
                      placeholder={t("name")}
                      label="name"
                      className="combined"
                      staric="staric"
                      register={register}
                      errors={errors}
                      validationRules={{
                        required: true,
                      }}
                    />
                    <FormElementWrapper className="combined pt-3">
                      <Box className="formEleContainer">
                        <LabelStyled
                          className="labelForm"
                          size="15px"
                          color="var(--text)"
                        >
                          {t("type")}
                          <span className="staric">*</span>
                        </LabelStyled>
                        <Box className="w-100">
                          <SelectionDropdown
                            name="organization_type_id"
                            placeholder={t("select_type")}
                            options={typeOptions}
                            value={watch("organization_type_id")}
                            register={register}
                            errors={errors}
                            validationRules={{
                              required: true,
                            }}
                          />
                          {errors.organization_type_id?.type === "required" && (
                            <ValidationErrorText
                              text={
                                t(errors.organization_type_id?.message) ||
                                t("Pleaseselectone")
                              }
                            />
                          )}
                        </Box>
                      </Box>
                    </FormElementWrapper>
                    <ValidatedInput
                      name="sector"
                      type="input"
                      fieldType="text"
                      placeholder={t("Sector")}
                      label="Sector"
                      className="combined"
                      staric="staric"
                      register={register}
                      errors={errors}
                      validationRules={{
                        required: true,
                      }}
                    />
                    <ValidatedInput
                      name="description"
                      type="textarea"
                      fieldType="text"
                      placeholder={"description"}
                      label="description"
                      className="combined"
                      errors={errors}
                      register={register}
                      validationRules={{
                        required: false,
                      }}
                    />
                    <SeparatorBorder className="mt-2" />
                    <Box className="innerFoHead">
                      <SubHeadingStyled size="16px" color="var(--text)">
                        {t("location")}
                      </SubHeadingStyled>
                    </Box>
                    <FormElementWrapper className="combined pt-3">
                      <Box className="formEleContainer">
                        <LabelStyled
                          className="labelForm"
                          size="15px"
                          color="var(--text)"
                        >
                          {t("Country")}
                          {/* <span className="staric">*</span> */}
                        </LabelStyled>
                        <Box className="w-100">
                          <SelectionDropdown
                            name="country_id"
                            className="countryDropdown"
                            placeholder={t("select_country")}
                            options={countries}
                            value={watch("country_id")}
                            register={register}
                            errors={errors}
                            validationRules={{
                              required: false,
                            }}
                          />
                          {errors.country_id?.type === "required" && (
                            <ValidationErrorText
                              text={
                                t(errors.country_id?.message) ||
                                t("Pleaseselectone")
                              }
                            />
                          )}
                        </Box>
                      </Box>
                    </FormElementWrapper>
                    <FormElementWrapper className="combined">
                      <Box className="formEleContainer">
                        <LabelStyled
                          style={{
                            marginBottom:
                              window.innerHeight <= 768 ? "-25px" : "",
                          }}
                          className="labelForm"
                          size="15px"
                          color="var(--text)"
                        >
                          {t("City")}
                          {/* <span className="staric">*</span> */}
                        </LabelStyled>
                        <Box className="w-100">
                          <LazyLoadDropdown
                            name="city_id"
                            control={control}
                            placeholder={t("select_city")}
                            options={cities}
                            filteredOptions={filteredCities}
                            // value={watch("city_id")}
                            onSearch={handleSearch}
                            onLoadMore={handleLoadMore}
                            register={register}
                            loading={citiesLoading}
                            errors={errors}
                            validationRules={{ required: false }}
                          />
                        </Box>
                      </Box>
                    </FormElementWrapper>
                    <ValidatedInput
                      name="address"
                      type="input"
                      fieldType="text"
                      placeholder={"Address"}
                      label="Address"
                      className="combined"
                      // staric={true}
                      errors={errors}
                      register={register}
                      validationRules={{
                        required: false,
                      }}
                    />
                    <SeparatorBorder className="mt-2" />
                    <Box className="innerFoHead">
                      <SubHeadingStyled size="16px" color="var(--text)">
                        {t("ContactDetails")}
                      </SubHeadingStyled>
                    </Box>
                    <PhoneInputField
                      name="contact_phone"
                      label={"ContactNo"}
                      placeholder={"ContactNo"}
                      // staric="staric"
                      className={"border-0"}
                      register={register}
                      control={control}
                      errors={errors}
                      validationRules={{
                        required: false,
                      }}
                    />
                    <ValidatedInput
                      name="contact_email"
                      type="input"
                      fieldType="text"
                      placeholder={t("email")}
                      label="email"
                      className="combined"
                      // staric="staric"
                      errors={errors}
                      register={register}
                      validationRules={{
                        required: false,
                      }}
                    />
                    <SeparatorBorder className="mt-2" />
                    <Box className="innerFoHead">
                      <SubHeadingStyled size="16px" color="var(--text)">
                        {t("currency")}
                      </SubHeadingStyled>
                    </Box>
                    <FormElementWrapper className="combined mb-3">
                      <Box className="formEleContainer d-flex align-items-center">
                        <LabelStyled
                          style={{
                            marginBottom:
                              window.innerHeight <= 768 ? "-25px" : "",
                          }}
                          className="labelForm"
                          size="15px"
                          color="var(--text)"
                        >
                          {t("currency")}
                          {/* <span className="staric">*</span> */}
                        </LabelStyled>
                        <Box className="w-100">
                          <LazyLoadDropdown
                            name="currency_id"
                            control={control}
                            placeholder={t("select_currency")}
                            options={currencyOptions}
                            filteredOptions={filteredCurrencies}
                            // value={watch("currency_id")}
                            onSearch={handleCurrencySearch}
                            register={register}
                            errors={errors}
                            validationRules={{ required: false }}
                          />
                        </Box>
                      </Box>
                    </FormElementWrapper>
                    <ValidatedInput
                      name="vat_number"
                      type="input"
                      fieldType="text"
                      placeholder={t("tax_number")}
                      label="tax_number"
                      className="combined"
                      // staric="staric"
                      errors={errors}
                      register={register}
                      validationRules={{
                        required: false,
                      }}
                    />
                    <ValidatedInput
                      name="vat_percentage"
                      type="input"
                      fieldType="number"
                      placeholder={t("tax_percentage")}
                      label="tax_percentage"
                      className="combined"
                      // staric="staric"
                      min={0}
                      max={100}
                      errors={errors}
                      register={register}
                      validationRules={{
                        required: false,
                      }}
                    />
                    <SeparatorBorder className="mt-2" />
                    <FormElementWrapper className="combined pt-3">
                      <Box className="formEleContainer">
                        <LabelStyled
                          className="labelForm"
                          size="15px"
                          color="var(--text)"
                        >
                          {t("UploadLogo")}
                        </LabelStyled>
                        <ValidationController
                          name="logo"
                          validationConfig={{
                            required: false,
                          }}
                          errors={errors}
                          control={control}
                          renderer={({ field }) => (
                            <ImageUpload
                              UploadImage="UploadLogo"
                              setLogoDelete={setLogoDelete}
                              onFileSelect={(file) => {
                                field.onChange(file); // Update form field
                                setLogoFile(file); // Update logo file state
                              }}
                              initialImage={logoFile} // Pass the preloaded image URL
                            />
                          )}
                        />
                      </Box>
                    </FormElementWrapper>

                    <SeparatorBorder className="mt-2" />
                    <Box className="commonBtns w-100">
                      <ButtonStyled
                        bg="var(--bgSecondary)"
                        label={t("Cancel")}
                        color="var(--text2)"
                        type="button"
                        onClick={() => navigate("/settings/establishment")}
                      ></ButtonStyled>
                      <ButtonStyled
                        label={t("Save")}
                        type="submit"
                        isLoading={loading}
                      ></ButtonStyled>
                    </Box>
                  </FormDataContainer>
                </Form>
              </Col>
            </Row>
          )}
        </Box>
      )}
    </SettingsWrapper>
  );
};

export default EditEstablishment;
